import { useEffect, useState } from "react";
import { authPost, invitationRefusalMessage } from "../lib/authApi.ts";

// The accept-invitation landing. The mailed link arrives with `?id=`; the
// page accepts through the auth library when signed in and offers sign-in or
// sign-up otherwise. See ADR-0042.

type AcceptInvitationPageProps = {
  invitationId: string | null;
  // Where to go once accepted.
  onAccepted: () => void;
};

type Phase =
  | { kind: "checking" }
  | { kind: "signed-out" }
  | { kind: "accepting" }
  | { kind: "failed"; message: string }
  | { kind: "accepted" };

/**
 * Accepts the invitation named in the URL. Signed out, it shows the sign-in
 * and create-account doors; signed in, it accepts and calls `onAccepted`, or
 * names the refusal (cancelled, expired, a different email, already a member).
 * Rendered by the public entry at `/accept-invitation`.
 */
export function AcceptInvitationPage({
  invitationId,
  onAccepted,
}: AcceptInvitationPageProps): JSX.Element {
  const [phase, setPhase] = useState<Phase>({ kind: "checking" });

  useEffect(() => {
    if (!invitationId) return;
    let cancelled = false;
    (async () => {
      const me = await fetch("/api/me", { credentials: "same-origin" }).catch(() => null);
      if (cancelled) return;
      if (!me || me.status === 401) {
        setPhase({ kind: "signed-out" });
        return;
      }
      setPhase({ kind: "accepting" });
      try {
        await authPost("/organization/accept-invitation", { invitationId });
        if (cancelled) return;
        setPhase({ kind: "accepted" });
        onAccepted();
      } catch (err) {
        if (!cancelled) setPhase({ kind: "failed", message: invitationRefusalMessage(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invitationId, onAccepted]);

  if (!invitationId) {
    return (
      <div className="auth-card">
        <h1>Invitation</h1>
        <div className="notice warn" role="alert">
          <div>This invitation link is incomplete. Ask the person who invited you for a new one.</div>
        </div>
      </div>
    );
  }

  const here = `/accept-invitation?id=${encodeURIComponent(invitationId)}`;

  if (phase.kind === "signed-out") {
    return (
      <div className="auth-card">
        <h1>You've been invited</h1>
        <p className="lede">
          Sign in with the address the invitation was sent to, or create an
          account with it. Either way you land in the Organisation that invited
          you.
        </p>
        <div className="btn-row">
          <a
            className="btn primary lg block"
            href={`/login?next=${encodeURIComponent(here)}`}
          >
            Sign in
          </a>
        </div>
        <div className="btn-row">
          <a
            className="btn lg block"
            href={`/sign-up?invitation=${encodeURIComponent(invitationId)}`}
          >
            Create an account
          </a>
        </div>
      </div>
    );
  }

  if (phase.kind === "failed") {
    return (
      <div className="auth-card">
        <h1>Couldn't accept the invitation</h1>
        <div className="notice bad" role="alert">
          <div>{phase.message}</div>
        </div>
        <p className="hint">
          If you are signed in as a different address, <a href="/login">sign in</a>{" "}
          as the one that was invited. If the invitation has expired, ask for a new one.
        </p>
      </div>
    );
  }

  return (
    <div className="auth-card">
      <h1>Accepting your invitation…</h1>
      <p className="lede" aria-live="polite">
        {phase.kind === "accepted" ? "Done. Taking you in." : "One moment."}
      </p>
    </div>
  );
}
